import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isFinite from 'lodash/isFinite';
import isEmpty from 'lodash/isEmpty';
import without from 'lodash/without';
import { prop } from 'ramda';
import Checkbox from '../../common/Checkbox';
import { AUDIENCE_TYPE } from '../constants';
import { fullName } from '../../common/helpers/users';
import Tooltip from '../../common/tooltip';
import { getByType } from '../selectors';

const audienceNameMap = {
  [AUDIENCE_TYPE.all]: 'All customers',
  [AUDIENCE_TYPE.regular]: 'Regulars',
  [AUDIENCE_TYPE.lapsing]: 'Lapsed',
  [AUDIENCE_TYPE.new]: 'New clients',
  [AUDIENCE_TYPE.service]: 'Service',
  [AUDIENCE_TYPE.prospective]: 'Prospective clients',
};

const BARBER_SORT_PRIORITY = 5;

const audienceSortMap = {
  [AUDIENCE_TYPE.new]: 1,
  [AUDIENCE_TYPE.service]: 2,
  [AUDIENCE_TYPE.regular]: 3,
  [AUDIENCE_TYPE.lapsing]: 4,
  [AUDIENCE_TYPE.barber]: BARBER_SORT_PRIORITY,
};

const audienceDescriptionMap = {
  [AUDIENCE_TYPE.regular]: 'Clients with at least two visits to the shop and the last visit in the past 6 weeks, with valid contact information', // eslint-disable-line max-len
  [AUDIENCE_TYPE.lapsing]: 'Clients whose last appointment was between 6 and 12 weeks ago, with valid contact information', // eslint-disable-line max-len
  [AUDIENCE_TYPE.prospective]: "Contacts who haven't booked yet. Target them to convert interest into bookings.",
};

const getAudienceName = (audienceItem = {}) => getByType(
  fullName,
  prop('label'),
  item => audienceNameMap[item.name],
)(audienceItem);

const getAudienceCustomers = ({ customers } = {}) => {
  if (!isFinite(customers)) {
    return '';
  }
  return `(${customers})`;
};

const getAudienceText = (audienceItem = {}) => `${getAudienceName(audienceItem)} ${getAudienceCustomers(audienceItem)}`;

const isAllSelected = (selected = []) => selected.includes(AUDIENCE_TYPE.all);

const getAudienceSortPriority = audienceKey => audienceSortMap[audienceKey] || BARBER_SORT_PRIORITY;
const sortAudienceKeys = (a, b) => getAudienceSortPriority(a) - getAudienceSortPriority(b);
const getAudienceKeys = (audience = {}) => {
  const audienceKeys = Object.keys(audience).sort(sortAudienceKeys);
  return without(audienceKeys, AUDIENCE_TYPE.all);
};

const buttonClassName = isSelected =>
  classnames('c-btn c-btn--white c-btn--active-branding u-1/1', {
    'is-active': isSelected,
  });

const SelectAudience = ({
  audience,
  selected,
  selectAll,
  selectCustom,
  toggleAudience,
  newCustomerWeeks,
  changeNewCustomerWeeks,
  services,
  serviceCustomerGroupServiceId,
  changeServiceCustomerGroupServiceId,
}) => (
  <div>
    <h2 className="c-heading">Select Your Audience</h2>
    <p className="epsilon u-type--light">How would you like to reach your clients?</p>
    <div className="o-flexbox u-mt u--ml">
      <div className="o-flexbox__item u-pl u-flex-1">
        <button
          type="button"
          className={buttonClassName(isAllSelected(selected))}
          onClick={selectAll}
          disabled={!audience[AUDIENCE_TYPE.all]}
        >
          {audience[AUDIENCE_TYPE.all] ? getAudienceText(audience[AUDIENCE_TYPE.all]) : 'All customers' }
        </button>
      </div>
      <div className="o-flexbox__item u-flex-1 u-pl">
        <button type="button" className={buttonClassName(!isAllSelected(selected))} onClick={selectCustom}>
          Custom
        </button>
      </div>
    </div>
    <ul className="layout u-mt" display-if={!isAllSelected(selected) && !isEmpty(audience)}>
      {getAudienceKeys(audience).map(key => (
        <li className="layout__item" key={key}>
          <div className="layout layout--middle layout--small layout--auto">
            <div className="layout__item">
              <Checkbox checked={selected.includes(key)} onChange={toggleAudience(audience[key])}>
                <span className="delta">{getAudienceText(audience[key])}</span>
                <Tooltip display-if={audienceDescriptionMap[key]}>
                  {audienceDescriptionMap[key]}
                </Tooltip>
              </Checkbox>
            </div>

            <div className="layout__item" display-if={key === AUDIENCE_TYPE.new}>
              <select
                className="c-select c-select--small"
                onChange={changeNewCustomerWeeks}
                value={newCustomerWeeks}
                {...{}}
              >
                <option value={1}>1 week</option>
                <option value={2}>2 weeks</option>
                <option value={3}>3 weeks</option>
                <option value={4}>4 weeks</option>
                <option value={5}>5 weeks</option>
                <option value={6}>6 weeks</option>
              </select>
            </div>

            <div className="layout__item" display-if={key === AUDIENCE_TYPE.service}>
              <select
                className="c-select c-select--small"
                onChange={changeServiceCustomerGroupServiceId}
                value={serviceCustomerGroupServiceId}
                {...{}}
              >
                {services.map(service => (
                  <option value={service.id} key={service.id}>{service.name}</option>
                ))}
              </select>
            </div>
          </div>
        </li>
      ))}
    </ul>
  </div>
);
SelectAudience.propTypes = {
  audience: PropTypes.objectOf(
    PropTypes.shape({
      type: PropTypes.string,
      customers: PropTypes.number,
    })
  ),
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectAll: PropTypes.func.isRequired,
  selectCustom: PropTypes.func.isRequired,
  toggleAudience: PropTypes.func.isRequired,
  newCustomerWeeks: PropTypes.number.isRequired,
  changeNewCustomerWeeks: PropTypes.func.isRequired,
  services: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  serviceCustomerGroupServiceId: PropTypes.string.isRequired,
  changeServiceCustomerGroupServiceId: PropTypes.func.isRequired,
};
SelectAudience.defaultProps = {
  audience: {},
  services: [],
};

export default SelectAudience;
